import { motion, AnimatePresence } from "framer-motion";
import { Check, Copy, ListTree } from "lucide-react";
import { toast } from "sonner";
import { FAMILY_CONDITIONS } from "@/lib/preventionData";

const BLUE = "#4C9EDA";
const DEEP = "#2E6FA3";
const INK = "#122534";

export default function Family({ picked, onToggle }) {
  const list = picked || [];
  const flags = FAMILY_CONDITIONS.filter((c) => list.includes(c.k));

  const copy = () => {
    navigator.clipboard?.writeText(`Family history to raise with a doctor\n\n${flags.map((f) => `— ${f.label}: ${f.flag}`).join("\n\n")}`);
    toast("Copied. Take it to the appointment.");
  };

  return (
    <section className="relative" style={{ background: "#F2F7FB" }} data-testid="prevention-family">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20">
        <div className="max-w-xl mb-9">
          <p className="text-[10px] tracking-[0.24em] uppercase" style={{ color: "rgba(18,37,52,0.4)" }}>Family history</p>
          <h2 className="font-display text-[32px] sm:text-[44px] leading-[1.02] mt-2" style={{ color: INK }}>What happened to them changes what applies to you</h2>
          <p className="text-[15px] mt-3" style={{ color: "rgba(18,37,52,0.6)" }}>Tick anything that happened to a parent, brother or sister. Each one has a specific consequence, and most of them are never mentioned in a ten minute appointment.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {FAMILY_CONDITIONS.map((c, i) => {
              const on = list.includes(c.k);
              return (
                <motion.button key={c.k} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: Math.min(i * 0.04, 0.3) }}
                  onClick={() => onToggle(c.k)} className="text-left rounded-2xl px-4 py-4 flex items-start gap-3 transition-all hover:-translate-y-0.5"
                  style={on ? { background: `${BLUE}18`, border: `1px solid ${BLUE}66` } : { background: "rgba(255,255,255,0.8)", border: "1px solid rgba(18,37,52,0.07)" }}
                  data-testid={`family-${c.k}`}>
                  <span className="w-[18px] h-[18px] rounded-md flex items-center justify-center shrink-0 mt-0.5"
                    style={on ? { background: DEEP, color: "#fff" } : { border: "1.5px solid rgba(18,37,52,0.2)" }}>{on && <Check size={11} />}</span>
                  <span className="text-[14px] leading-snug" style={{ color: INK }}>{c.label}</span>
                </motion.button>
              );
            })}
          </div>

          <div className="lg:col-span-6">
            <div className="rounded-[1.8rem] p-7" style={{ background: "linear-gradient(160deg, #0F2A3D, #143850)", boxShadow: "0 40px 80px -60px rgba(18,37,52,0.9)" }}>
              <div className="flex items-center gap-2.5">
                <ListTree size={16} style={{ color: BLUE }} />
                <p className="font-display text-[20px] text-white">What this changes for you</p>
                <p className="ml-auto font-display text-2xl tabular-nums" style={{ color: BLUE }}>{flags.length}</p>
              </div>
              <div className="mt-5 space-y-3 min-h-[150px]">
                <AnimatePresence initial={false}>
                  {flags.length === 0 ? (
                    <motion.p key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-[14px] text-white/45">
                      Nothing ticked. If genuinely nobody close to you has had any of these, that is itself worth knowing, and this section stays quiet.
                    </motion.p>
                  ) : (
                    flags.map((f) => (
                      <motion.div key={f.k} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} className="rounded-2xl px-4 py-3.5" style={{ background: "rgba(255,255,255,0.07)" }}>
                        <p className="text-[10px] tracking-[0.14em] uppercase font-semibold" style={{ color: BLUE }}>{f.label}</p>
                        <p className="text-[13.5px] mt-1.5 text-white/75 leading-relaxed">{f.flag}</p>
                      </motion.div>
                    ))
                  )}
                </AnimatePresence>
              </div>
              <button onClick={copy} disabled={!flags.length} className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3.5 text-sm font-semibold disabled:opacity-35"
                style={{ background: BLUE, color: "#0B1C28" }} data-testid="family-copy">
                <Copy size={13} /> Copy it for the appointment
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
